import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon, ServerIcon, ClockIcon, SearchIcon, FileTextIcon, BarChartIcon, ZapIcon, PenToolIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { PricingCard } from '../components/pricing/PricingCard';
import { WebsiteCareComparisonTable } from '../components/pricing/WebsiteCareComparisonTable';
export function WebsiteCare() {
  const [tableExpanded, setTableExpanded] = useState(false);
  const toggleTable = () => {
    setTableExpanded(!tableExpanded);
  };
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleEssentialCareClick = () => {
    window.open('https://buy.stripe.com/14A8wJ1w0cICfXA10caZi06', '_blank');
  };
  const handleTotalGrowthClick = () => {
    window.open('https://buy.stripe.com/9B628l7UobEy9zcfV6aZi07', '_blank');
  };
  const handleOneOffFixClick = () => {
    window.open('https://buy.stripe.com/28EdR3fmQ8smaDgbEQaZi08', '_blank');
  };
  return <>
      <Helmet data-id="element-2566">
        <title data-id="element-2567">Website Care & Maintenance | Bright Pixxel Studio</title>
        <meta name="description" content="Keep your site secure, updated and fast with monthly care plans. Hosting, backups, SSL and support for UK businesses. See plans from £129/month." data-id="element-2568" />
        <link rel="canonical" href="https://bright-pixxel.com/website-care" data-id="element-2569" />
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-2570" />
        <meta property="og:type" content="website" data-id="element-2571" />
        <meta property="og:title" content="Website Care & Maintenance | Bright Pixxel Studio" data-id="element-2572" />
        <meta property="og:description" content="Keep your site secure, updated and fast with monthly care plans. Hosting, backups, SSL and support for UK businesses. See plans from £129/month." data-id="element-2573" />
        <meta property="og:url" content="https://bright-pixxel.com/website-care" data-id="element-2574" />
        <meta property="og:site_name" content="Bright Pixxel Studio" data-id="element-2575" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2576" />
        <meta property="og:image:width" content="1200" data-id="element-2577" />
        <meta property="og:image:height" content="630" data-id="element-2578" />
        <meta property="og:image:type" content="image/png" data-id="element-2579" />
        <meta property="og:image:alt" content="Bright Pixxel Studio - Website Maintenance Services" data-id="element-2580" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-2581" />
        <meta name="twitter:title" content="Website Care & Maintenance | Bright Pixxel Studio" data-id="element-2582" />
        <meta name="twitter:description" content="Keep your site secure, updated and fast with monthly care plans. Hosting, backups, SSL and support for UK businesses. See plans from £129/month." data-id="element-2583" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2584" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studio - Website Maintenance Services" data-id="element-2585" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-[clamp(56px,8vw,72px)] pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2586">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2587">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2588">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-4xl font-bold mb-4 text-white" data-id="element-2589">
              Website Care Plans That Keep Your Site Secure, Fast and Up to Date
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg text-white/90 mb-8" data-id="element-2590">
              Professional, engaging content that connects with your audience
              and drives results. From website copy to blog posts, we create
              content that works.
            </motion.p>
          </div>
        </div>
      </section>
      {/* Pricing Cards Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-2591">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2592">
          <div className="max-w-7xl mx-auto" data-id="element-2593">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2594">
              <PricingCard title="Essential" subtitle="Basic Maintenance" price="£129/month" popular={false} features={['Security monitoring', 'Backups', 'Uptime monitoring', 'Plugin and theme updates', 'Yearly hosting, email and domain costs remain payable by the client']} iconSrc="https://images.unsplash.com/photo-1633613286991-611fe299c4be?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="Essential website care plan icon" ctaText="Get Started" onClick={scrollToContact} data-id="element-2595" />
              <PricingCard title="Growth" subtitle="Complete Solution" price="£299/month" popular={true} features={['Everything in Essential', 'All yearly technical costs covered in full (hosting, business email, SSL, domain renewals)', 'Content updates', 'Minor changes within a monthly allowance', 'Technical support', 'Monthly report']} iconSrc="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="Growth website care plan icon" ctaText="Most Popular" onClick={scrollToContact} data-id="element-2596" />
              <PricingCard title="Performance" subtitle="Advanced Growth" price="£449/month" popular={false} features={['Everything in Growth', 'Monthly SEO improvements', 'Google Business Profile posts', 'Quarterly conversion review']} iconSrc="https://images.unsplash.com/photo-1581093450021-4a7360e9a6b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="Performance website care plan icon" ctaText="Get Started" onClick={scrollToContact} data-id="element-2597" />
            </div>

            <div className="mt-12 text-center max-w-3xl mx-auto" data-id="element-2598">
              <p className="text-gray-600 dark:text-gray-300 italic" data-id="element-2599">
                Major redesigns and significant new functionality are quoted
                separately. Large content changes are agreed in advance.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Features Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2601">
          <div className="max-w-4xl mx-auto" data-id="element-2602">
            <div className="text-center mb-12" data-id="element-2603">
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2604">
                What's Included in Your Care Plan
              </motion.h2>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-2605">
                All our care plans include these essential features to keep your
                website running smoothly.
              </motion.p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2606">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl shadow-md text-center" data-id="element-2607">
                <div className="w-12 h-12 mx-auto bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2608">
                  <ServerIcon size={24} className="text-[#0DC3DC]" data-id="element-2609" />
                </div>
                <h3 className="text-lg font-semibold mb-2" data-id="element-2610">Hosting</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2611">
                  Reliable hosting with SSL certificates and domain management
                  included.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl shadow-md text-center" data-id="element-2612">
                <div className="w-12 h-12 mx-auto bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2613">
                  <ShieldCheckIcon size={24} className="text-[#0DC3DC]" data-id="element-2614" />
                </div>
                <h3 className="text-lg font-semibold mb-2" data-id="element-2615">Security</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2616">
                  Round-the-clock monitoring, malware scanning, and regular
                  security updates.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl shadow-md text-center" data-id="element-2617">
                <div className="w-12 h-12 mx-auto bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2618">
                  <SearchIcon size={24} className="text-[#0DC3DC]" data-id="element-2619" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white" data-id="element-2620">SEO</h3>
                <p className="text-white/90" data-id="element-2621">
                  Ongoing optimisation to maintain and improve search rankings.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl shadow-md text-center" data-id="element-2622">
                <div className="w-12 h-12 mx-auto bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2623">
                  <FileTextIcon size={24} className="text-[#0DC3DC]" data-id="element-2624" />
                </div>
                <h3 className="text-lg font-semibold mb-2" data-id="element-2625">Content</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2626">
                  Regular content updates to keep your site fresh and relevant.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl shadow-md text-center" data-id="element-2627">
                <div className="w-12 h-12 mx-auto bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2628">
                  <BarChartIcon size={24} className="text-[#0DC3DC]" data-id="element-2629" />
                </div>
                <h3 className="text-lg font-semibold mb-2" data-id="element-2630">Reports</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2631">
                  Monthly analytics reports to track your site's performance.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.6
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl shadow-md text-center" data-id="element-2632">
                <div className="w-12 h-12 mx-auto bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2633">
                  <ClockIcon size={24} className="text-[#0DC3DC]" data-id="element-2634" />
                </div>
                <h3 className="text-lg font-semibold mb-2" data-id="element-2635">Support</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2636">
                  Priority email and phone support when you need assistance.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* Trust Section */}
      <section className="py-12 bg-white dark:bg-[#1E1E1E]" data-id="element-2637">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2638">
          <div className="max-w-4xl mx-auto" data-id="element-2639">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-[#212C34] p-8 rounded-xl shadow-md text-center" data-id="element-2640">
              <h3 className="text-2xl font-bold mb-4 text-white" data-id="element-2641">
                Your Website, Your Control
              </h3>
              <p className="text-white/90 text-lg mb-6" data-id="element-2642">
                Full ownership of your site + transparent pricing + free domain
                & SSL for the first year
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2643">
                <div className="flex items-center justify-center" data-id="element-2644">
                  <CheckIcon size={20} className="text-[#0DC3DC] mr-2 flex-shrink-0" data-id="element-2645" />
                  <span className="text-white/90" data-id="element-2646">100% Site Ownership</span>
                </div>
                <div className="flex items-center justify-center" data-id="element-2647">
                  <CheckIcon size={20} className="text-[#0DC3DC] mr-2 flex-shrink-0" data-id="element-2648" />
                  <span className="text-white/90" data-id="element-2649">Transparent Pricing</span>
                </div>
                <div className="flex items-center justify-center" data-id="element-2650">
                  <CheckIcon size={20} className="text-[#0DC3DC] mr-2 flex-shrink-0" data-id="element-2651" />
                  <span className="text-white/90" data-id="element-2652">
                    Free Domain & SSL (1st Year)
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2653">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2654">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2655">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="mb-8" data-id="element-2656">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" alt="Bright Pixxel Studio - Affordable, Creative, Digital Excellence" className="h-16 mx-auto" data-id="element-2657" />
            </motion.div>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl font-bold mb-4" data-id="element-2658">
              Ready to protect your website investment?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-2659">
              Get in touch today to discuss your website maintenance needs.
              We'll help you choose the right plan to keep your site secure,
              fast, and performing at its best.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-2660">
              <Button size="lg" onClick={scrollToContact} data-id="element-2661">
                Start Your Care Plan
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}